// src/components/PathSelectorModal.jsx
// Modal for selecting development path: Autopilot, Guided Journey, or Raw Save
import React from 'react';
import { X, Rocket, Target, Save, Lock, Mail, Loader2 } from 'lucide-react';

function PathSelectorModal({ 
  isOpen, 
  onClose, 
  onSelectPath, 
  description, 
  /** US-04 / US-05: true when user may use Autopilot/Guided per Firestore entitlement hint */
  canUseEnhancementProduct,
  /** Firestore summary subscription loading */
  entitlementLoading = false,
  /** Short footer line (complimentary credit, denial, or error) */
  entitlementFooterHint = null,
  hideRawSave = false, // For existing ideas, hide Raw Save option
  /** US-11: complimentary free pass requires verified email — show banner and lock AI paths until verified */
  needsEmailVerification = false,
  onResendVerificationEmail,
  onRefreshAuthAfterVerification,
  verificationResendBusy = false,
  verificationReloadBusy = false,
  verificationNotice = null,
  /** US-13: self-serve subscribe */
  showSubscribeCta = false,
  showManageBillingCta = false,
  onSubscribe,
  onManageBilling,
  billingActionBusy = false,
}) {
  const canEnhance =
    typeof canUseEnhancementProduct === 'boolean' ? canUseEnhancementProduct : false;
  /** AI paths blocked when entitlement says no, loading, or US-11 unverified email on complimentary pass */
  const isAiPathDisabled =
    entitlementLoading || !canEnhance || needsEmailVerification;
  if (!isOpen) return null;

  const handleSelectPath = (path) => {
    // Note: Don't call onClose() here - the parent's onSelectPath handler
    // already closes the modal. Calling onClose() would clear pendingDescription
    // before child modals (like GuidedJourneyModal) can use it.
    onSelectPath(path);
  };

  const paths = [
    {
      id: 'autopilot',
      icon: Rocket,
      title: 'Autopilot',
      subtitle: 'Let AI develop this idea automatically',
      description: 'AI generates title, description, tags, subtasks, and plan steps in one pass.',
      color: 'bg-gradient-to-br from-purple-500 to-indigo-600',
      hoverColor: 'hover:from-purple-600 hover:to-indigo-700',
      requiresAdmin: true
    },
    {
      id: 'guided',
      icon: Target,
      title: 'Guided Journey',
      subtitle: 'Develop step-by-step with AI assistance',
      description: '3-step process: refine title & description, then tags & tasks, then plan.',
      color: 'bg-gradient-to-br from-blue-500 to-cyan-600',
      hoverColor: 'hover:from-blue-600 hover:to-cyan-700',
      requiresAdmin: true
    },
    {
      id: 'raw',
      icon: Save,
      title: 'Raw Save',
      subtitle: 'Save as-is for later',
      description: 'Store your idea immediately without AI processing. Develop it later.',
      color: 'bg-gradient-to-br from-gray-500 to-gray-600',
      hoverColor: 'hover:from-gray-600 hover:to-gray-700',
      requiresAdmin: false,
      hidden: hideRawSave
    }
  ];

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" 
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
        <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Choose Your Path
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                How would you like to develop this idea?
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* US-11: verify email for complimentary AI pass */}
          {needsEmailVerification && (
            <div className="px-4 sm:px-6 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
              <div className="flex gap-2">
                <Mail className="w-5 h-5 text-amber-700 dark:text-amber-300 flex-shrink-0 mt-0.5" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                    Verify your email to use your complimentary AI pass
                  </p>
                  <p className="text-xs text-amber-800/90 dark:text-amber-200/90 mt-1">
                    Check your inbox for the verification link (sent via our mail provider), or resend it below. After you verify, tap refresh so we can unlock Autopilot and Guided Journey.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {typeof onResendVerificationEmail === 'function' && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onResendVerificationEmail();
                        }}
                        disabled={verificationResendBusy || verificationReloadBusy}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {verificationResendBusy ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Mail className="w-3.5 h-3.5" />
                        )}
                        Resend verification email
                      </button>
                    )}
                    {typeof onRefreshAuthAfterVerification === 'function' && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRefreshAuthAfterVerification();
                        }}
                        disabled={verificationResendBusy || verificationReloadBusy}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-amber-600 text-amber-900 dark:text-amber-100 hover:bg-amber-100/80 dark:hover:bg-amber-900/40 disabled:opacity-50"
                      >
                        {verificationReloadBusy ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : null}
                        I&apos;ve verified — refresh
                      </button>
                    )}
                  </div>
                  {verificationNotice ? (
                    <p className="text-xs text-amber-900 dark:text-amber-200 mt-2">{verificationNotice}</p>
                  ) : null}
                </div>
              </div>
            </div>
          )}

          {/* Idea Preview */}
          <div className="px-4 sm:px-6 py-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
              "{description}"
            </p>
          </div>

          {/* Path Options */}
          <div className="p-4 sm:p-6 space-y-3">
            {paths.filter(path => !path.hidden).map((path) => {
              const Icon = path.icon;
              const isDisabled =
                path.requiresAdmin && isAiPathDisabled;
              
              return (
                <button
                  key={path.id}
                  onClick={() => !isDisabled && handleSelectPath(path.id)}
                  disabled={isDisabled}
                  className={`w-full p-4 rounded-xl text-left transition-all ${
                    isDisabled 
                      ? 'opacity-60 cursor-not-allowed bg-gray-100 dark:bg-gray-700/50' 
                      : `${path.color} ${path.hoverColor} text-white shadow-lg hover:shadow-xl hover:scale-[1.02]`
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${isDisabled ? 'bg-gray-200 dark:bg-gray-600' : 'bg-white/20'}`}>
                      {isDisabled ? (
                        <Lock className="w-6 h-6 text-gray-500 dark:text-gray-400" />
                      ) : (
                        <Icon className="w-6 h-6" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-semibold ${isDisabled ? 'text-gray-700 dark:text-gray-300' : ''}`}>
                          {path.title}
                        </h3>
                        {isDisabled && (
                          <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 rounded-full">
                            Unavailable
                          </span>
                        )}
                      </div>
                      <p className={`text-sm mt-0.5 ${isDisabled ? 'text-gray-500 dark:text-gray-400' : 'text-white/90'}`}>
                        {path.subtitle}
                      </p>
                      <p className={`text-xs mt-2 ${isDisabled ? 'text-gray-400 dark:text-gray-500' : 'text-white/70'}`}>
                        {path.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Footer: entitlement hint (US-04) + billing CTAs (US-13) */}
          {(entitlementFooterHint || showSubscribeCta || showManageBillingCta) && (
            <div className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-3">
              {entitlementFooterHint && (
                <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                  {entitlementFooterHint}
                </p>
              )}
              <div className="flex flex-wrap justify-center gap-2">
                {showSubscribeCta && typeof onSubscribe === 'function' && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSubscribe();
                    }}
                    disabled={billingActionBusy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
                  >
                    {billingActionBusy ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : null}
                    Subscribe — $10/month
                  </button>
                )}
                {showManageBillingCta && typeof onManageBilling === 'function' && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onManageBilling();
                    }}
                    disabled={billingActionBusy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    {billingActionBusy ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : null}
                    Manage subscription
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default PathSelectorModal;
